import { Navigate, Link } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import RoleBadge from "@/components/RoleBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalendarCheck, AlertTriangle, Phone, ClipboardCheck, Activity, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useWithBack } from "@/lib/nav";
import { toast } from "sonner";

const TILE_DEFS = [
  {
    key: "due_soon_30d",
    label: "Due Soon (30d)",
    icon: CalendarCheck,
    tone: "text-blue-600",
    to: "/scheduling/due",
    hint: "Machines due for service in the next 30 days.",
  },
  {
    key: "overdue",
    label: "Overdue",
    icon: AlertTriangle,
    tone: "text-red-600",
    to: "/scheduling/due",
    hint: "Machines whose next due date has passed.",
  },
  {
    key: "call_to_book",
    label: "Call to Book",
    icon: Phone,
    tone: "text-amber-600",
    to: "/scheduling/call-to-book",
    hint: "On-demand customers needing outreach.",
  },
  {
    key: "actions_required",
    label: "Actions Required",
    icon: ClipboardCheck,
    tone: "text-purple-600",
    to: "/jobs?actions_required=true",
    hint: "Completed jobs flagged as needing office follow-up.",
  },
];

const KIND_LABELS = {
  created: "Created",
  edited: "Edited",
  assigned: "Assigned",
  unassigned: "Unassigned",
  status_changed: "Status changed",
  deactivated: "Deactivated",
  closed: "Closed",
  reopened: "Sent back",
  aborted: "Aborted",
  email_retried: "Email retried",
  report_regenerated: "Report regenerated",
  started: "Started",
  completed: "Completed",
  slot_signed: "Slot signed",
};

function kindLabel(kind) {
  return KIND_LABELS[kind] || (kind || "").replace(/_/g, " ") || "Activity";
}

const SCOPE_STORAGE_KEY = "recent-activity-scope";

export default function Dashboard() {
  const { user } = useAuth();
  const withBackHref = useWithBack();
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [blockers, setBlockers] = useState([]);
  const [blockersLoading, setBlockersLoading] = useState(true);

  const isEngineer = user?.role === "engineer";
  const canSeeTeam = user?.role === "super_admin" || user?.role === "manager";
  const canSeeBlockers = user?.role === "super_admin" || user?.role === "manager" || user?.role === "office";

  // Persisted per user via localStorage; default "mine" for non-privileged.
  const [scope, setScope] = useState(() => {
    try {
      const stored = localStorage.getItem(SCOPE_STORAGE_KEY);
      if (stored === "team" || stored === "mine") return stored;
    } catch {
      /* localStorage unavailable */
    }
    return "mine";
  });

  const setScopeAndPersist = useCallback((next) => {
    setScope(next);
    try { localStorage.setItem(SCOPE_STORAGE_KEY, next); } catch { /* ignore */ }
  }, []);

  // If a user without team access has "team" persisted, silently coerce to mine.
  useEffect(() => {
    if (!canSeeTeam && scope === "team") setScopeAndPersist("mine");
  }, [canSeeTeam, scope, setScopeAndPersist]);

  useEffect(() => {
    if (isEngineer) return; // will redirect
    api.get("/dashboard/counters")
      .then(({ data }) => setCounts(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [isEngineer]);

  useEffect(() => {
    if (isEngineer) return;
    setActivityLoading(true);
    const effectiveScope = canSeeTeam ? scope : "mine";
    api.get("/dashboard/recent-activity", { params: { limit: 20, scope: effectiveScope } })
      .then(({ data }) => setActivity(data.items || []))
      .catch(() => setActivity([]))
      .finally(() => setActivityLoading(false));
  }, [isEngineer, canSeeTeam, scope]);

  // V1.5 #9 — Submission Blockers widget (office+manager+super_admin).
  useEffect(() => {
    if (isEngineer || !canSeeBlockers) return;
    setBlockersLoading(true);
    api.get("/dashboard/submission-blockers", { params: { days: 30 } })
      .then(({ data }) => setBlockers(data.rows || []))
      .catch(() => setBlockers([]))
      .finally(() => setBlockersLoading(false));
  }, [isEngineer, canSeeBlockers]);

  if (isEngineer) return <Navigate to="/my/jobs" replace />;

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" data-testid="dashboard-title">Dashboard</h1>
        <RoleBadge role={user?.role} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {TILE_DEFS.map((t) => {
          const Icon = t.icon;
          const value = counts?.[t.key];
          const isOverdueActive = t.key === "overdue" && (value ?? 0) > 0 && !loading;
          return (
            <Link
              key={t.key}
              to={t.to}
              data-testid={`stat-${t.key}`}
              className={`focus:outline-none focus:ring-2 focus:ring-primary rounded-md ${isOverdueActive ? "ring-1 ring-red-500/40" : ""}`}
            >
              <Card className={`rounded-md hover:border-primary/50 hover:shadow-sm transition-colors cursor-pointer h-full ${isOverdueActive ? "border-red-500/40" : ""}`}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    {t.label}
                  </CardTitle>
                  <Icon className={`h-4 w-4 ${t.tone} ${isOverdueActive ? "animate-pulse" : ""}`} />
                </CardHeader>
                <CardContent>
                  <div
                    className={`text-2xl font-bold ${isOverdueActive ? "text-red-600 animate-pulse" : ""}`}
                    data-testid={`stat-value-${t.key}`}
                  >
                    {loading ? "…" : (value ?? 0)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{t.hint}</p>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>

      <Card className="rounded-md" data-testid="dashboard-recent-activity">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            {canSeeTeam && scope === "team" ? "Team Recent Activity" : "My Recent Activity"}
          </CardTitle>
          <div className="flex items-center gap-3">
            {canSeeTeam && (
              <div
                role="tablist"
                aria-label="Activity scope"
                className="inline-flex items-center bg-muted p-0.5 rounded-md"
                data-testid="recent-activity-scope-toggle"
              >
                {["mine", "team"].map((s) => {
                  const isActive = scope === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setScopeAndPersist(s)}
                      data-testid={`recent-activity-scope-${s}`}
                      className={[
                        "px-2.5 py-1 text-xs rounded-sm capitalize transition-colors",
                        isActive
                          ? "bg-background shadow-sm text-foreground font-medium"
                          : "text-muted-foreground hover:text-foreground",
                      ].join(" ")}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            )}
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {activityLoading ? "Loading…" : `${activity.length} entries`}
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {activityLoading ? (
            <div className="p-6 text-xs text-muted-foreground" data-testid="dashboard-activity-loading">Loading recent activity…</div>
          ) : activity.length === 0 ? (
            <div className="p-6 text-xs text-muted-foreground italic" data-testid="dashboard-activity-empty">
              {scope === "team"
                ? "No team activity in the recent window."
                : "No recent activity yet — actions you take on jobs (creating, assigning, closing, editing, etc.) will appear here."}
            </div>
          ) : (
            <ol className="divide-y divide-border">
              {activity.map((a, i) => (
                <li
                  key={`${a.job_id}-${a.at}-${i}`}
                  className="p-3 flex items-start justify-between gap-3 hover:bg-muted/30 transition-colors"
                  data-testid={`dashboard-activity-row-${i}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {scope === "team" && a.actor_name && (
                        <span
                          className="text-[10px] uppercase tracking-wider font-bold text-primary bg-primary/10 border border-primary/20 rounded-sm px-1.5 py-0.5"
                          data-testid={`dashboard-activity-actor-${i}`}
                        >
                          {a.actor_name}
                        </span>
                      )}
                      <span className="uppercase tracking-wider font-bold text-[10px] text-muted-foreground">
                        {kindLabel(a.kind)}
                      </span>
                      <Link
                        to={withBackHref(`/jobs/${a.job_id}`)}
                        className="text-sm font-mono hover:text-primary"
                        data-testid={`dashboard-activity-job-${i}`}
                      >
                        {a.job_number}
                      </Link>
                      {a.job_title && (
                        <span className="text-xs text-muted-foreground truncate">— {a.job_title}</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {a.message}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                      {a.customer_name}{a.site_name ? ` · ${a.site_name}` : ""}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground shrink-0">{formatDate(a.at)}</div>
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {canSeeBlockers && (
        <Card className="rounded-md" data-testid="dashboard-submission-blockers">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 gap-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-primary" />
              Submission Blockers
              <span className="text-xs font-normal text-muted-foreground">— last 30 days</span>
            </CardTitle>
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {blockersLoading ? "Loading…" : `${blockers.length} row${blockers.length === 1 ? "" : "s"}`}
            </span>
          </CardHeader>
          <CardContent className="p-0">
            {blockersLoading ? (
              <div className="p-6 text-xs text-muted-foreground" data-testid="dashboard-blockers-loading">Loading…</div>
            ) : blockers.length === 0 ? (
              <div className="p-6 text-xs text-muted-foreground italic" data-testid="dashboard-blockers-empty">
                No submission blockers in the last 30 days.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="dashboard-blockers-table">
                <thead className="bg-muted/50 border-b border-border">
                  <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    <th className="text-left py-2 px-3">Engineer</th>
                    <th className="text-left py-2 px-3">Blocker</th>
                    <th className="text-right py-2 px-3 whitespace-nowrap">Count (30d)</th>
                  </tr>
                </thead>
                <tbody>
                  {blockers.map((row, i) => (
                    <tr
                      key={`${row.engineer_id}-${row.code}-${i}`}
                      data-testid={`dashboard-blockers-row-${i}`}
                      className="border-b border-border last:border-0 hover:bg-muted/30"
                    >
                      <td className="py-2 px-3">
                        <div className="text-sm font-medium">{row.engineer_name || row.engineer_username || "—"}</div>
                        {row.engineer_username && row.engineer_name && (
                          <div className="text-xs text-muted-foreground font-mono">{row.engineer_username}</div>
                        )}
                      </td>
                      <td className="py-2 px-3 text-muted-foreground">
                        {row.code_label}
                        <div className="text-[10px] font-mono text-muted-foreground/70 mt-0.5">{row.code}</div>
                      </td>
                      <td className="py-2 px-3 text-right font-mono tabular-nums text-sm font-bold" data-testid={`dashboard-blockers-count-${i}`}>
                        {row.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
